import { computed } from 'vue';
import { useStore } from 'vuex';
import { GeoJSON } from 'ol/format';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Circle, Fill, Icon, Stroke, Style } from 'ol/style';

export default function useVectorLayers(layers: MapLayer[]) {
  const store = useStore();
  const locationCoordinates = computed(() => store.state.projectsMap.locationCoordinates);

  function createIconStyle(layer: MapLayer) {
    return {
      image: new Icon({
        anchor: [0.5, 1],
        src: layer.src,
      }),
    };
  }

  function createLineStyle(layer: MapLayer, feature: MapFeature) {
    if (layer.field) {
      const layerName: string = feature.get(layer.field).toString();
      const color: string = layer.color[layerName];
      const lineCap: CanvasLineCap = layer.lineCap ? layer.lineCap[layerName] : 'round';
      const lineDash: number[] = layer.lineDash ? layer.lineDash[layerName] : null;
      return {
        stroke: new Stroke({
          color,
          lineCap,
          lineDash,
          width: layer.width,
        }),
      };
    }
    return {
      stroke: new Stroke({
        color: layer.color,
        width: layer.width,
      }),
    };
  }

  function createPointStyle(layer: MapLayer) {
    return {
      image: new Circle({
        fill: new Fill({
          color: layer.color,
        }),
        radius: layer.radius,
      }),
    };
  }

  function createStyle(layer: MapLayer, feature: MapFeature) {
    const typeStyle: MapStyle = {
      line: createLineStyle.bind(null, layer, feature),
      point: createPointStyle.bind(null, layer, feature),
      icon: createIconStyle.bind(null, layer, feature),
    };

    return new Style(typeStyle[layer.type]());
  }

  async function fetchLayerSource(url: string) {
    const andOr = url.includes('?') ? '&' : '?';
    let endpoint = `${url}${andOr}distance=0`;

    if (locationCoordinates.value) {
      endpoint += `&${new URLSearchParams(locationCoordinates.value)}`;
    } else {
      endpoint += '&x=&y=';
    }

    const response = await fetch(endpoint, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    if (!response.ok) {
      try {
        const msg = await response.text();
        const parsedMessage = JSON.parse(msg);
        throw new Error(parsedMessage.message);
      } catch {
        throw new Error('Error: Feature layer was unable to load');
      }
    }
    const layer = await response.json();
    return layer;
  }

  function createLayer(layer: MapLayer) {
    const source = new VectorSource({
      format: new GeoJSON(),
      loader: (extent, _resolution, _projection, success, failure) => {
        fetchLayerSource(layer.url)
          .then((response) => {
            const reponseObj = Object.keys(response);
            if (source && reponseObj.length !== 0) {
              const format = source.getFormat();
              if (format) {
                const features = format.readFeatures(response);
                source.addFeatures(features);
                if (success) {
                  success(features);
                }
              }
            }
          })
          .catch((error) => {
            source.removeLoadedExtent(extent);
            store.commit('projectsMap/SET_LAYER_ERROR', error.message);
            if (failure) {
              failure();
            }
          });
      },
    });

    return new VectorLayer({
      source,
      style: createStyle.bind(this, layer),
      properties: {
        selectable: layer.selectable,
      },
    });
  }

  return {
    vectorLayers: layers.map(createLayer),
  };
}
